import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, CheckSquare, Receipt, FileSignature, FolderOpen, Calendar, UserPlus, X, Clock, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const ICON = {
  task_overdue: CheckSquare, task_today: CheckSquare, invoice_overdue: Receipt,
  retainer_unsigned: FileSignature, docs_missing: FolderOpen, appointment: Calendar, unassigned: UserPlus,
};
const DOT = { high: "bg-rose-500", normal: "bg-amber-500", low: "bg-slate-400" };

// Reads the current route so the nudge follows the workflow: a case's next step on a case page,
// a client's on a client page, otherwise the top things to do overall.
function contextFromPath(pathname) {
  let m = pathname.match(/\/clients\/([^/]+)/);
  if (m) return { client_id: m[1] };
  m = pathname.match(/\/applications\/([^/]+)/);
  if (m) return { application_id: m[1] };
  return {};
}

export function NextActionPopup() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(true);
  const loc = useLocation();
  const nav = useNavigate();

  const load = useCallback(() => {
    const params = contextFromPath(loc.pathname);
    api.get("/next-actions", { params }).then((r) => { setData(r.data); setIdx(0); }).catch(() => setData({ enabled: false, items: [] }));
  }, [loc.pathname]);
  useEffect(() => { load(); }, [load]);

  if (!data || !data.enabled) return null;
  const items = data.items || [];
  if (items.length === 0) return null;
  const pos = loc.pathname.startsWith("/admin/messages") ? "top-20 right-5" : "bottom-5 right-5"; // keep clear of the chat composer

  const safeIdx = Math.min(idx, items.length - 1);
  const it = items[safeIdx];
  const Icon = ICON[it.type] || Sparkles;

  const act = async (key, fn, after) => {
    const remaining = items.filter((x) => x.key !== key);
    setData((d) => ({ ...d, items: remaining }));
    setIdx((i) => Math.max(0, Math.min(i, remaining.length - 1)));
    try { await fn(); if (after) after(); } catch (e) { toast.error(apiErr(e)); load(); }
  };
  const dismiss = (key) => act(key, () => api.post("/next-actions/dismiss", { key }));
  const snooze = (key) => act(key, () => api.post("/next-actions/snooze", { key, days: 1 }), () => toast.success("Snoozed until tomorrow"));

  if (!open) {
    return (
      <button data-testid="next-action-bubble" onClick={() => setOpen(true)}
        className={`fixed ${pos} z-40 inline-flex items-center gap-2 bg-primary text-white rounded-full shadow-lg px-4 py-3 text-sm font-semibold`}>
        <Sparkles className="h-4 w-4" /> Next step
        <span className="bg-white/25 rounded-full px-1.5 text-xs">{items.length}</span>
      </button>
    );
  }

  return (
    <div data-testid="next-action-popup" className={`fixed ${pos} z-40 w-[320px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden`}>
      <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border-b border-slate-100">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="font-head font-bold text-slate-900 text-sm">Next step</span>
        {items.length > 1 && <span className="text-[11px] text-slate-400">{safeIdx + 1} of {items.length}</span>}
        <button data-testid="next-action-collapse" onClick={() => setOpen(false)} title="Hide" className="ml-auto text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-4">
        <div className="flex items-start gap-2.5">
          <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${DOT[it.priority] || DOT.normal}`} />
          <Icon className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">{it.title}</div>
            {it.detail && <div className="text-xs text-slate-500 mt-0.5">{it.detail}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button data-testid="next-action-open" onClick={() => { nav(it.link); }} className="inline-flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded-lg text-xs font-semibold"><ArrowRight className="h-3.5 w-3.5" /> Open</button>
          <button data-testid="next-action-snooze" onClick={() => snooze(it.key)} title="Snooze until tomorrow" className="inline-flex items-center gap-1 border border-slate-200 text-slate-600 px-2.5 py-1.5 rounded-lg text-xs font-semibold"><Clock className="h-3.5 w-3.5" /> Snooze</button>
          <button data-testid="next-action-dismiss" onClick={() => dismiss(it.key)} title="Dismiss" className="inline-flex items-center gap-1 border border-slate-200 text-slate-600 px-2.5 py-1.5 rounded-lg text-xs font-semibold"><X className="h-3.5 w-3.5" /> Done</button>
          {items.length > 1 && (
            <div className="ml-auto flex items-center">
              <button onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)} className="text-slate-400 hover:text-slate-700 p-1"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => setIdx((i) => (i + 1) % items.length)} className="text-slate-400 hover:text-slate-700 p-1"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
